import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { Loader2 } from "lucide-react";

interface SubscriptionGuardProps {
  children: ReactNode;
}

/**
 * Subscription guard logic:
 * 1. If user's profile has is_internal = true → bypass (founder / employees)
 * 2. If user was INVITED into an org (not the owner) → bypass (rides on org's subscription)
 * 3. If user OWNS the org → check for active Stripe subscription on that org
 * 4. No active subscription on owned org → redirect to /subscribe
 */
export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  // Check if user is internal (founder/employee)
  const { data: isInternal, isLoading: internalLoading } = useQuery({
    queryKey: ["is-internal", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_internal")
        .eq("id", user!.id)
        .single();

      if (error || !data) return false;
      return data.is_internal === true;
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  });

  // Check if user is an org owner or was invited (non-owner)
  const { data: orgRole, isLoading: orgLoading } = useQuery({
    queryKey: ["org-role-check", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_members")
        .select("organization_id, role")
        .eq("user_id", user!.id)
        .not("joined_at", "is", null)
        .order("joined_at", { ascending: true })
        .limit(1);

      if (error || !data || data.length === 0) {
        return { hasOrg: false, isOwner: false, orgId: null };
      }

      return {
        hasOrg: true,
        isOwner: data[0].role === "owner",
        orgId: data[0].organization_id,
      };
    },
    enabled: !!user && !isInternal,
    staleTime: 5 * 60 * 1000,
  });

  // Only check Stripe subscription if user is an org owner (not internal, not invited)
  const { isLoading: subLoading, isSubscribed } = useSubscription();
  const needsSubCheck = !isInternal && orgRole?.isOwner === true;

  // Loading states
  if (authLoading || internalLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Internal users always get in
  if (isInternal) {
    return <>{children}</>;
  }

  if (orgLoading) {
    return <LoadingScreen />;
  }

  // No org at all (shouldn't happen due to auto-create, but safety net)
  if (!orgRole?.hasOrg) {
    return <Navigate to="/subscribe" state={{ from: location }} replace />;
  }

  // Invited member (non-owner) → rides on org subscription, no check needed
  if (!orgRole.isOwner) {
    return <>{children}</>;
  }

  // Org owner → check subscription
  if (needsSubCheck && subLoading) {
    return <LoadingScreen />;
  }

  if (needsSubCheck && !isSubscribed) {
    return <Navigate to="/subscribe" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground font-mono">Calibrating access...</p>
      </div>
    </div>
  );
}
